import React, { useCallback, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import { format } from 'date-fns';
import ar from 'date-fns/locale/ar-SA';
import fr from 'date-fns/locale/fr';
import { useTranslation } from 'react-i18next';
import 'react-datepicker/dist/react-datepicker.css';
import './CustomComponents.css';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getBrandDatePickerLocale } from '../../utils/brandCurrency';
import { isSaudiBrand } from '../../../products/saudi/utils/isSaudiBrand';

registerLocale('ar', ar);
registerLocale('ar-SA', ar);
registerLocale('fr', fr);

/**
 * Parse stored value (YYYY-MM-DD, YYYY-MM-DDTHH:mm local, or ISO) — avoids wrong midnight when time is present.
 */
function parseValueToDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const s = String(value).trim();
  // ISO with Z: use built-in (UTC → local display in picker)
  if (/\d{4}-\d{2}-\d{2}T.*Z$/i.test(s)) {
    const dt = new Date(s);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  // Local datetime: YYYY-MM-DDTHH:mm (optional seconds) — not Z
  const ymdt = s.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (ymdt) {
    const y = parseInt(ymdt[1], 10);
    const m = parseInt(ymdt[2], 10) - 1;
    const d = parseInt(ymdt[3], 10);
    const hh = parseInt(ymdt[4], 10);
    const min = parseInt(ymdt[5], 10);
    const sec = ymdt[6] != null ? parseInt(ymdt[6], 10) : 0;
    const dt = new Date(y, m, d, hh, min, sec, 0);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const y = parseInt(ymd[1], 10);
    const m = parseInt(ymd[2], 10) - 1;
    const d = parseInt(ymd[3], 10);
    const dt = new Date(y, m, d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const dt = new Date(s);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/**
 * Insert slashes while typing: dd/mm/yyyy
 *
 * @param {string} raw
 * @param {{ trailingSlash?: boolean }} [options] - If false (user is deleting), do not add trailing `/`
 *   after day or month so backspace can remove digits without the slash reappearing.
 */
function formatDigitsToDdMmYyyy(raw, options = {}) {
  const { trailingSlash = true } = options;
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length === 0) return '';
  if (digits.length === 1) return digits;

  if (!trailingSlash) {
    if (digits.length === 2) return digits;
    if (digits.length === 3) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    if (digits.length === 4) return `${digits.slice(0, 2)}/${digits.slice(2, 4)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  }

  if (digits.length === 2) return `${digits}/`;
  if (digits.length === 3) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  if (digits.length === 4) return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function isInputDeletionEvent(e) {
  if (e.nativeEvent instanceof InputEvent && e.nativeEvent.inputType) {
    const t = e.nativeEvent.inputType;
    return /^deleteContent/.test(t) || t === 'deleteByCut' || t === 'deleteByDrag';
  }
  return false;
}

/**
 * Native input wrapper: applies dd/mm/aaaa mask on every input event (reliable vs onChangeRaw alone).
 */
const MaskedDateTextInput = React.forwardRef(function MaskedDateTextInput(props, ref) {
  const { onInput, value, ...rest } = props;
  const digitLenRef = useRef(0);

  useEffect(() => {
    const n = String(value ?? '')
      .replace(/\D/g, '')
      .slice(0, 8).length;
    digitLenRef.current = n;
  }, [value]);

  const handleInput = (e) => {
    const el = e.target;
    if (el instanceof HTMLInputElement) {
      const before = el.value;
      const digits = before.replace(/\D/g, '').slice(0, 8);
      const prevDigitLen = digitLenRef.current;
      digitLenRef.current = digits.length;

      const fromInputType = isInputDeletionEvent(e);
      const fromLength = digits.length < prevDigitLen;
      const isDelete = fromInputType || fromLength;

      const formatted = formatDigitsToDdMmYyyy(before, { trailingSlash: !isDelete });
      if (formatted !== before) {
        const cursor = el.selectionStart ?? before.length;
        el.value = formatted;
        const delta = formatted.length - before.length;
        const next = Math.min(formatted.length, Math.max(0, cursor + delta));
        requestAnimationFrame(() => {
          try {
            el.setSelectionRange(next, next);
          } catch {
            /* ignore */
          }
        });
      }
    }
    if (typeof onInput === 'function') onInput(e);
  };

  return (
    <input
      {...rest}
      value={value}
      ref={ref}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      onInput={handleInput}
    />
  );
});

/**
 * CustomDatePicker - Qawaem-styled date picker (react-datepicker)
 *
 * @param {string} props.value - Date (YYYY-MM-DD or ISO) or empty
 * @param {Function} props.onChange - (dateString) => void — YYYY-MM-DD or '' (or ISO slice for time mode)
 * @param {boolean} [props.showMonthYearDropdowns=true] - Month + year dropdowns (recommended; custom header hides them)
 * @param {boolean} [props.maskInput=true] - Auto-insert / while typing dd/mm/yyyy
 */
const CustomDatePicker = ({
  value,
  onChange,
  placeholder,
  className = '',
  disabled = false,
  minDate,
  maxDate,
  showTimeSelect = false,
  dateFormat: dateFormatProp,
  /** When true (default), use native month/year selects instead of only arrow navigation */
  showMonthYearDropdowns = true,
  /** Auto-format input with slashes (dd/mm/yyyy) while typing */
  maskInput = true,
  /**
   * Custom scroll panels for month/year with glass styling (CSS overrides default 50% width).
   * 'select' mode uses native OS dropdowns which may show black backgrounds on Windows.
   */
  dropdownMode = 'scroll',
  /** Only used when dropdownMode is 'scroll' */
  scrollableYearDropdown = false,
  yearDropdownItemNumber = 24,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation();
  const saudiBrand = isSaudiBrand();
  const datePickerLocale = getBrandDatePickerLocale();
  const dateFnsLocale = datePickerLocale === 'ar' ? ar : fr;
  const selectedDate = parseValueToDate(value);

  const dateFormat =
    dateFormatProp ?? (saudiBrand ? (showTimeSelect ? 'yyyy/MM/dd HH:mm' : 'yyyy/MM/dd') : 'dd/MM/yyyy');

  const defaultPlaceholder =
    placeholder ??
    (showTimeSelect
      ? t('common.select_date_time', saudiBrand ? 'yyyy/MM/dd HH:mm' : 'dd/MM/yyyy HH:mm')
      : t('common.select_date', saudiBrand ? 'yyyy/MM/dd' : 'dd/MM/yyyy'));

  const handleChange = (date) => {
    if (!date) {
      onChange('');
      return;
    }
    if (showTimeSelect) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const hh = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      onChange(`${y}-${m}-${d}T${hh}:${min}`);
    } else {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      onChange(`${y}-${m}-${d}`);
    }
  };

  /** Legacy GPS-style header — only when month/year dropdowns are disabled */
  const renderCustomHeader = useCallback(
    ({
      date,
      decreaseMonth,
      increaseMonth,
      prevMonthButtonDisabled,
      nextMonthButtonDisabled,
    }) => {
      const displayDate = date instanceof Date ? date : new Date();
      return (
        <div className="flex items-center justify-between px-4 py-3">
          <button
            type="button"
            onClick={decreaseMonth}
            disabled={prevMonthButtonDisabled}
            className="p-1 hover:bg-white/10 rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-5 h-5 text-white" />
          </button>
          <div className="text-white font-semibold text-base">
            {format(displayDate, 'MMMM yyyy', { locale: dateFnsLocale })}
          </div>
          <button
            type="button"
            onClick={increaseMonth}
            disabled={nextMonthButtonDisabled}
            className="p-1 hover:bg-white/10 rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-5 h-5 text-white" />
          </button>
        </div>
      );
    },
    [dateFnsLocale]
  );

  /** Backup mask when not using customInput (same rules as MaskedDateTextInput) */
  const handleChangeRaw = useCallback(
    (event) => {
      if (!maskInput || showTimeSelect) return;
      const target = event?.target;
      if (!(target instanceof HTMLInputElement)) return;
      const before = target.value;
      const formatted = formatDigitsToDdMmYyyy(before);
      if (formatted !== before) {
        const pos = target.selectionStart ?? before.length;
        target.value = formatted;
        const delta = formatted.length - before.length;
        const next = Math.min(formatted.length, Math.max(0, pos + delta));
        requestAnimationFrame(() => {
          try {
            target.setSelectionRange(next, next);
          } catch {
            /* ignore */
          }
        });
      }
    },
    [maskInput, showTimeSelect]
  );

  const useMaskedInput = maskInput && !showTimeSelect;

  const calendarClassName = showMonthYearDropdowns
    ? saudiBrand
      ? 'gps-calendar gps-calendar--dropdowns qawaem-light-calendar'
      : 'gps-calendar gps-calendar--dropdowns'
    : saudiBrand
      ? 'gps-calendar qawaem-light-calendar'
      : 'gps-calendar';

  const inputClassName = saudiBrand
    ? 'w-full bg-white/70 border border-forest-200/50 rounded-xl px-4 py-2.5 text-forest-900 focus:outline-none focus:ring-2 focus:ring-[#6e9b6e]/40 transition-all gps-datepicker qawaem-light-datepicker'
    : 'w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all gps-datepicker';

  return (
    <div className={className} data-testid={dataTestId}>
      <DatePicker
        selected={selectedDate}
        onChange={handleChange}
        onChangeRaw={useMaskedInput ? undefined : handleChangeRaw}
        customInput={useMaskedInput ? <MaskedDateTextInput /> : undefined}
        locale={datePickerLocale}
        dateFormat={showTimeSelect ? (saudiBrand ? 'yyyy/MM/dd HH:mm' : 'dd/MM/yyyy HH:mm') : dateFormat}
        placeholderText={defaultPlaceholder}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        showTimeSelect={showTimeSelect}
        timeFormat="HH:mm"
        timeIntervals={15}
        className={inputClassName}
        calendarClassName={calendarClassName}
        wrapperClassName="w-full"
        popperClassName={saudiBrand ? 'date-picker-popper qawaem-period-datepicker-popper' : 'date-picker-popper'}
        popperPlacement="bottom-start"
        popperProps={{
          strategy: 'absolute',
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
            {
              name: 'preventOverflow',
              options: {
                rootBoundary: 'document',
                tether: false,
                altAxis: true,
              },
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['top-start', 'bottom-start'],
              },
            },
          ],
        }}
        showMonthDropdown={showMonthYearDropdowns}
        showYearDropdown={showMonthYearDropdowns}
        dropdownMode={dropdownMode}
        scrollableYearDropdown={scrollableYearDropdown}
        yearDropdownItemNumber={yearDropdownItemNumber}
        renderCustomHeader={showMonthYearDropdowns ? undefined : renderCustomHeader}
        portalId={saudiBrand ? 'qawaem-datepicker-portal' : 'root'}
        autoComplete="off"
      />
    </div>
  );
};

export default CustomDatePicker;
